'use client'

import React, { ReactElement } from 'react'
import { Room } from '@/types/Misc'
import { Image } from '@/components/common/Image'
import { useTranslations } from 'next-intl'
import ChevronRightIcon from '@/icons/font-awesome/sharp-light/chevron-right.svg'
import classNames from 'classnames'
import { Picture } from '@/components/common/picture/Picture'
import { Sizes } from '@/components/common/picture/Sizes'

export default function HotelRoom(props: {
  room: Room
  onSelect?: (roomCode: string) => void
  selected?: boolean
  price?: string
  showMoreDetailsClicked?: () => void
  withinSlider: boolean
}): ReactElement | null {
  const t = useTranslations('Hotel.HotelRooms')

  function handleMoreDetailsClick(event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) {
    event.stopPropagation()
    event.preventDefault()
    props.showMoreDetailsClicked?.()
  }

  return (
    <div
      className={classNames({
        'p-0.5': !props.selected,
      })}
    >
      <div
        className={classNames(
          'flex h-full flex-grow cursor-pointer flex-col overflow-hidden rounded-md border-solid bg-white shadow-md',
          {
            'border-2 border-primary': props.selected,
          },
        )}
        onClick={
          props.onSelect
            ? () => {
                props.onSelect?.(props.room.code)
              }
            : handleMoreDetailsClick
        }
      >
        <div className="h-[165px]">
          {props.room.roomImages &&
            props.room.roomImages.length > 0 &&
            (props.withinSlider ? (
              <Image
                filename={props.room.roomImages[0].fileName}
                width={262}
                height={165}
                className="h-[165px] w-full"
                alt={props.room.roomImages[0].caption || ''}
              />
            ) : (
              <Picture
                src={props.room.roomImages[0].fileName}
                alt={props.room.roomImages[0].caption || ''}
                size={{
                  [Sizes.xs]: [195, 304, 165],
                  [Sizes.sm]: [305, 308, 165],
                  [Sizes.md]: [309, 355, 165],
                  [Sizes.lg]: [255, 310, 165],
                  [Sizes.xl]: [311, 311, 165],
                }}
                staticPosition
                cornerRounding={' '}
              />
            ))}
          {(!props.room.roomImages || props.room.roomImages.length === 0) && (
            <div className="flex h-[165px] bg-stone-200">
              <div className="flex h-full w-full items-center justify-center">
                <div className="text-sm text-stone-400">{t('pictureDelayText')}</div>
              </div>
            </div>
          )}
        </div>
        <div className="box-border flex h-[160px] flex-grow flex-col overflow-hidden p-3">
          <div className="flex-grow">
            <h3 className="mb-1 font-bold leading-normal text-stone-900">{props.room.name}</h3>
            {props.room.popularFeatures && props.room.popularFeatures.length > 0 && (
              <div className="mb-1 flex flex-row flex-wrap gap-1 text-xs leading-normal text-stone-600">
                {props.room.popularFeatures
                  .map((feature) => feature.text)
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>

          <div className="flex">
            <button
              className="align-center flex border-b border-white text-sm text-stone-600 hover:underline"
              onClick={handleMoreDetailsClick}
            >
              <span>{t('moreDetailsButtonLabel')}</span>
              <ChevronRightIcon className="ml-1 mt-1.5 h-3 w-3 fill-stone-600" />
            </button>
          </div>
          {props.price && (
            <p className="my-2 text-center font-bold text-stone-800">{props.price}</p>
          )}
        </div>
      </div>
    </div>
  )
}
